import { useEffect, useState } from "react";
import {
	Drawer,
	Button,
	IconButton,
	MenuItem,
	TextField,
	FormControlLabel,
	Switch,
	Box,
	Divider,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	Alert,
	Stack,
} from "@mui/material";
import { KeyboardDoubleArrowRightOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import useStyles from "./styles";
import SeletorClienteHost from "../seletorClienteHost";
import SeletorServicos from "../seletorServicos";
import FormTicketParams, {
	validarTicketParams,
	ERPS_COM_TICKET,
	ERP_ATIVO,
} from "../formTicketParams";
import ErpFilter from "../../../erp/ErpFilter";

const SEVERIDADES = ["informativo", "baixo", "medio", "alto", "critico"];
const METODOS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const PLACEHOLDERS =
	"{{host}} {{cliente}} {{alerta}} {{severidade}} {{duracao}} {{eventId}} {{dataHora}}";

// Tenta parsear JSON de um textarea; retorna { ok, valor, erro }.
function parseJsonOpcional(texto, rotulo) {
	if (!texto || !texto.trim()) return { ok: true, valor: undefined };
	try {
		return { ok: true, valor: JSON.parse(texto) };
	} catch (_) {
		return { ok: false, erro: `${rotulo}: JSON inválido` };
	}
}

export default function DrawerAutomacao({
	open,
	onClose,
	automacao, // se vier, é edição
	tipos = [],
	clientes = [],
	allHosts = [],
	// ERPs cadastrados ([] = nenhum: vale a variável ERP do front).
	erps = [],
	onSalvar,
	onTestar,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [nome, setNome] = useState("");
	const [ativo, setAtivo] = useState(true);
	const [escopo, setEscopo] = useState("GLOBAL");
	const [clienteId, setClienteId] = useState(null);
	const [hostId, setHostId] = useState(null);
	// [] = todos os serviços do cliente (padrão)
	const [servicoIds, setServicoIds] = useState([]);
	const [tipoAlertaId, setTipoAlertaId] = useState("");
	const [severidadeMin, setSeveridadeMin] = useState("");
	const [duracaoMinutos, setDuracaoMinutos] = useState(30);
	const [acao, setAcao] = useState("CRIAR_TICKET");
	const [cooldownMinutos, setCooldownMinutos] = useState(0);

	// config CRIAR_TICKET
	const [usarTextoNativo, setUsarTextoNativo] = useState(true);
	const [textoPersonalizado, setTextoPersonalizado] = useState("");
	const [assunto, setAssunto] = useState("");
	const [ticketParams, setTicketParams] = useState({});
	// ERP onde a regra CRIAR_TICKET abre o ticket (null sem ERP cadastrado).
	const [erpTicketId, setErpTicketId] = useState(null);

	// config CHAMAR_API
	const [metodo, setMetodo] = useState("POST");
	const [url, setUrl] = useState("");
	const [headersText, setHeadersText] = useState("");
	const [corpoTemplate, setCorpoTemplate] = useState("");

	const [erro, setErro] = useState("");
	const [erroCamposTicket, setErroCamposTicket] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const [testando, setTestando] = useState(false);
	const [resultadoTeste, setResultadoTeste] = useState(null);

	// Com ERPs cadastrados, o ticket sai do ERP escolhido na regra (só os que
	// criam ticket); sem cadastro, da variável do front, como antes.
	const erpsTicket = erps.filter((erp) => ERPS_COM_TICKET.includes(erp.tipo));
	const tipoTicket = erps.length
		? erpsTicket.find((erp) => erp.id === erpTicketId)?.tipo || null
		: ERP_ATIVO();
	const erpSuportaTicket = erps.length
		? erpsTicket.length > 0
		: ERPS_COM_TICKET.includes(ERP_ATIVO());
	// CRIAR_TICKET: só clientes do ERP da regra (sem erpId: cliente antigo,
	// o backend confere).
	const clientesDaRegra =
		acao === "CRIAR_TICKET" && erpTicketId
			? clientes.filter((c) => !c?.erpId || c.erpId === erpTicketId)
			: clientes;

	// Troca do ERP do ticket: os parâmetros (ids do ERP) e o cliente de outro
	// ERP deixam de valer.
	const trocarErpTicket = (id) => {
		setErpTicketId(id);
		setTicketParams({});
		const cliente = clientes.find((c) => c?.id === clienteId);
		if (cliente?.erpId && cliente.erpId !== id) {
			setClienteId(null);
			setHostId(null);
			setServicoIds([]);
		}
	};

	// Prefill ao abrir (edição ou criação).
	useEffect(() => {
		if (!open) return;
		setErro("");
		setErroCamposTicket(false);
		setResultadoTeste(null);
		setSalvando(false);
		setTestando(false);
		const a = automacao;
		setNome(a?.nome || "");
		setAtivo(a ? !!a.ativo : true);
		setEscopo(a?.escopo || "GLOBAL");
		setClienteId(a?.clienteId || null);
		setHostId(a?.hostId || null);
		setServicoIds((a?.servicoIds || []).map(String));
		setTipoAlertaId(a?.tipoAlertaId || "");
		setSeveridadeMin(a?.severidadeMin || "");
		setDuracaoMinutos(a?.duracaoMinutos ?? 30);
		setAcao(a?.acao || (erpSuportaTicket ? "CRIAR_TICKET" : "CHAMAR_API"));
		setCooldownMinutos(a?.cooldownMinutos ?? 0);

		const cfg = a?.config || {};
		setUsarTextoNativo(cfg.usarTextoNativo ?? true);
		setTextoPersonalizado(cfg.textoPersonalizado || "");
		setAssunto(cfg.assunto || "");
		setTicketParams(cfg.ticketParams || {});
		// Regra nova: o primeiro ERP com ticket (HubSoft antes). Regra salva: o
		// dela; regra antiga sem ERP só herda quando há um único ERP com ticket
		// (com mais de um, o operador escolhe).
		const ticketOrdenados = [...erpsTicket].sort(
			(x, y) => (y.tipo === "HUBSOFT") - (x.tipo === "HUBSOFT"),
		);
		setErpTicketId(
			a?.erpId ||
				(!a || ticketOrdenados.length === 1 ? ticketOrdenados[0]?.id : null) ||
				null,
		);
		setMetodo(cfg.metodo || "POST");
		setUrl(cfg.url || "");
		setHeadersText(cfg.headers ? JSON.stringify(cfg.headers, null, 2) : "");
		setCorpoTemplate(cfg.corpoTemplate || "");
	}, [open, automacao, erpSuportaTicket, erps]); // eslint-disable-line react-hooks/exhaustive-deps

	// Monta o config conforme a ação; lança string de erro se inválido.
	const montarConfig = () => {
		if (acao === "CRIAR_TICKET") {
			if (!usarTextoNativo && !textoPersonalizado.trim()) {
				throw "Texto personalizado é obrigatório quando não usa o texto nativo.";
			}
			if (erps.length && !erpTicketId) {
				throw "Escolha o ERP onde o ticket será aberto.";
			}
			const erroParams = validarTicketParams(ticketParams, tipoTicket);
			if (erroParams) {
				setErroCamposTicket(true);
				throw erroParams;
			}
			return {
				usarTextoNativo,
				textoPersonalizado: usarTextoNativo ? null : textoPersonalizado,
				assunto: assunto || null,
				ticketParams,
			};
		}
		// CHAMAR_API
		if (!/^https?:\/\//i.test(url.trim())) {
			throw "URL deve começar com http:// ou https://";
		}
		const h = parseJsonOpcional(headersText, "Headers");
		if (!h.ok) throw h.erro;
		return {
			metodo,
			url: url.trim(),
			headers: h.valor || {},
			corpoTemplate: corpoTemplate || null,
		};
	};

	const montarPayload = () => {
		const config = montarConfig();
		return {
			...(automacao?.id ? { id: automacao.id } : {}),
			nome,
			ativo,
			escopo,
			clienteId: escopo === "GLOBAL" ? null : clienteId,
			hostId: escopo === "HOST" ? hostId : null,
			// Só o escopo CLIENTE filtra por serviço; [] = todos.
			servicoIds: escopo === "CLIENTE" ? servicoIds : [],
			tipoAlertaId: tipoAlertaId || null,
			severidadeMin: severidadeMin || null,
			duracaoMinutos: Number(duracaoMinutos),
			acao,
			config,
			cooldownMinutos: Number(cooldownMinutos) || 0,
			// CHAMAR_API não depende de ERP (vale para todos).
			erpId: acao === "CRIAR_TICKET" ? erpTicketId : null,
		};
	};

	const validarBase = () => {
		if (!nome.trim()) return "Nome é obrigatório.";
		// O backend exige inteiro positivo; sem isto, 1.5 passava aqui e voltava 400.
		const dur = Number(duracaoMinutos);
		if (!Number.isInteger(dur) || dur <= 0) {
			return "Duração deve ser um número inteiro de minutos maior que 0.";
		}
		const cool = Number(cooldownMinutos);
		if (cooldownMinutos !== "" && (!Number.isInteger(cool) || cool < 0)) {
			return "Cooldown deve ser um número inteiro de minutos (0 ou mais).";
		}
		if (escopo !== "GLOBAL" && !clienteId) return "Selecione um cliente.";
		if (escopo === "HOST" && !hostId) return "Selecione um host.";
		return "";
	};

	const handleSalvar = async () => {
		const baseErro = validarBase();
		if (baseErro) return setErro(baseErro);
		let payload;
		try {
			payload = montarPayload();
		} catch (msg) {
			return setErro(String(msg));
		}
		setErro("");
		setErroCamposTicket(false);
		setSalvando(true);
		try {
			await onSalvar(payload);
		} finally {
			setSalvando(false);
		}
	};

	const handleTestar = async (executar) => {
		let payload;
		try {
			payload = montarPayload();
		} catch (msg) {
			return setErro(String(msg));
		}
		setErro("");
		setTestando(true);
		setResultadoTeste(null);
		try {
			const r = await onTestar({
				acao: payload.acao,
				config: payload.config,
				executar,
				erpId: payload.erpId || undefined,
			});
			setResultadoTeste(r);
		} catch (e) {
			setResultadoTeste({ erro: e?.response?.data?.message || e.message });
		} finally {
			setTestando(false);
		}
	};

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{ sx: styles.paper }}
		>
			<Box sx={styles.cabecalho}>
				<Box>
					<Typography sx={styles.titulo}>
						{automacao?.id ? "Editar automação" : "Nova automação"}
					</Typography>
					<Typography sx={styles.subtitulo}>
						Dispara uma ação quando o alerta persiste pelo tempo configurado
					</Typography>
				</Box>
				<Tooltip title="Fechar">
					<IconButton onClick={onClose} disabled={salvando}>
						<KeyboardDoubleArrowRightOutlined />
					</IconButton>
				</Tooltip>
			</Box>

			<Divider />

			<Box sx={styles.corpo}>
				{erro && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{erro}
					</Alert>
				)}

				<TextField
					fullWidth
					size="small"
					label="Nome da regra"
					value={nome}
					onChange={(e) => setNome(e.target.value)}
					sx={{ mb: 2 }}
				/>

				{/* Escopo */}
				<Typography variant="caption" color="text.secondary">
					Escopo
				</Typography>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={escopo}
					onChange={(_, v) => {
						if (!v) return;
						setEscopo(v);
						if (v === "GLOBAL") {
							setClienteId(null);
							setHostId(null);
						}
						if (v === "CLIENTE") setHostId(null);
						if (v !== "CLIENTE") setServicoIds([]);
					}}
					sx={{ display: "block", mt: 0.5, mb: 2 }}
				>
					<ToggleButton value="GLOBAL">Global</ToggleButton>
					<ToggleButton value="CLIENTE">Cliente</ToggleButton>
					<ToggleButton value="HOST">Host</ToggleButton>
				</ToggleButtonGroup>

				{escopo !== "GLOBAL" && (
					<Box sx={{ mb: 2 }}>
						<SeletorClienteHost
							clientes={clientesDaRegra}
							allHosts={allHosts}
							clienteId={clienteId}
							onClienteChange={(id) => {
								setClienteId(id);
								setServicoIds([]); // troca de cliente volta para "todos"
							}}
							hostId={hostId}
							onHostChange={setHostId}
							exigirHost={escopo === "HOST"}
						/>

						{escopo === "CLIENTE" && clienteId && (
							<Box sx={{ mt: 1 }}>
								<SeletorServicos
									clienteErpId={
										clientes.find((c) => c?.id === clienteId)?.clienteErpId
									}
									clienteId={clienteId}
									servicoIds={servicoIds}
									onChange={setServicoIds}
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Gatilho */}
				<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
					<TextField
						select
						fullWidth
						size="small"
						label="Tipo de alerta"
						value={tipoAlertaId}
						onChange={(e) => setTipoAlertaId(e.target.value)}
					>
						<MenuItem value="">Qualquer tipo</MenuItem>
						{tipos.map((t) => (
							<MenuItem key={t.id} value={t.id}>
								{t.nome}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						fullWidth
						size="small"
						label="Severidade mínima"
						value={severidadeMin}
						onChange={(e) => setSeveridadeMin(e.target.value)}
					>
						<MenuItem value="">Qualquer</MenuItem>
						{SEVERIDADES.map((s) => (
							<MenuItem key={s} value={s}>
								{s}
							</MenuItem>
						))}
					</TextField>
				</Stack>

				<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
					<TextField
						type="number"
						size="small"
						label="Disparar após (min)"
						value={duracaoMinutos}
						onChange={(e) => setDuracaoMinutos(e.target.value)}
						inputProps={{ min: 1 }}
						fullWidth
					/>
					<TextField
						type="number"
						size="small"
						label="Cooldown (min)"
						value={cooldownMinutos}
						onChange={(e) => setCooldownMinutos(e.target.value)}
						inputProps={{ min: 0 }}
						fullWidth
					/>
				</Stack>

				<Divider sx={{ my: 2 }} />

				{/* Ação */}
				<Typography variant="caption" color="text.secondary">
					Ação
				</Typography>
				<ToggleButtonGroup
					exclusive
					size="small"
					value={acao}
					onChange={(_, v) => {
						if (v) {
							setAcao(v);
							setResultadoTeste(null);
						}
					}}
					sx={{ display: "block", mt: 0.5, mb: 2 }}
				>
					<ToggleButton value="CRIAR_TICKET" disabled={!erpSuportaTicket}>
						Criar ticket
					</ToggleButton>
					<ToggleButton value="CHAMAR_API">Chamar API externa</ToggleButton>
				</ToggleButtonGroup>

				{acao === "CRIAR_TICKET" ? (
					<>
						{/* Com mais de um ERP que cria ticket, o operador escolhe em
						    qual: a regra só alcança hosts dos clientes dele. */}
						{erpsTicket.length > 1 && (
							<ErpFilter
								erps={erpsTicket}
								value={erpTicketId}
								onChange={(id) => id && trocarErpTicket(id)}
								limpavel={false}
								label="ERP do ticket"
								id="erp-automacao"
								sx={{ width: "100%", mb: 2 }}
							/>
						)}
						<FormControlLabel
							control={
								<Switch
									checked={usarTextoNativo}
									onChange={(e) => setUsarTextoNativo(e.target.checked)}
								/>
							}
							label="Usar texto nativo do alerta (Zabbix)"
						/>
						{!usarTextoNativo && (
							<TextField
								fullWidth
								multiline
								minRows={2}
								size="small"
								label="Texto personalizado"
								value={textoPersonalizado}
								onChange={(e) => setTextoPersonalizado(e.target.value)}
								helperText={`Placeholders: ${PLACEHOLDERS}`}
								sx={{ mt: 1, mb: 2 }}
							/>
						)}
						<TextField
							fullWidth
							size="small"
							label="Assunto (opcional)"
							value={assunto}
							onChange={(e) => setAssunto(e.target.value)}
							sx={{ mt: 1, mb: 2 }}
						/>
						{(!erps.length || erpTicketId) && (
							<FormTicketParams
								value={ticketParams}
								onChange={setTicketParams}
								erroCampos={erroCamposTicket}
								erp={tipoTicket}
								erpId={erpTicketId}
							/>
						)}
					</>
				) : (
					<>
						<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
							<TextField
								select
								size="small"
								label="Método"
								value={metodo}
								onChange={(e) => setMetodo(e.target.value)}
								sx={{ minWidth: 110 }}
							>
								{METODOS.map((m) => (
									<MenuItem key={m} value={m}>
										{m}
									</MenuItem>
								))}
							</TextField>
							<TextField
								fullWidth
								size="small"
								label="URL"
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								placeholder="https://..."
							/>
						</Stack>
						<TextField
							fullWidth
							multiline
							minRows={2}
							size="small"
							label="Headers (JSON, opcional)"
							value={headersText}
							onChange={(e) => setHeadersText(e.target.value)}
							helperText='Ex.: { "Authorization": "Bearer ..." }'
							sx={{ mb: 2 }}
						/>
						<TextField
							fullWidth
							multiline
							minRows={3}
							size="small"
							label="Corpo (template)"
							value={corpoTemplate}
							onChange={(e) => setCorpoTemplate(e.target.value)}
							helperText={`Placeholders: ${PLACEHOLDERS}`}
						/>
					</>
				)}

				<Divider sx={{ my: 2 }} />

				<FormControlLabel
					control={
						<Switch
							checked={ativo}
							onChange={(e) => setAtivo(e.target.checked)}
						/>
					}
					label={ativo ? "Regra ativa" : "Regra inativa"}
				/>
				{ativo && (
					<Alert severity="warning" sx={{ mt: 1 }}>
						{acao === "CRIAR_TICKET"
							? "Regra ativa: o motor criará tickets automaticamente no ERP quando o gatilho for atingido."
							: "Regra ativa: o motor chamará a API externa automaticamente quando o gatilho for atingido."}
						{" "}
						Vale para alertas que começarem <b>depois</b> de salvar — alertas já
						abertos agora não disparam esta regra.
					</Alert>
				)}

				{/* Teste (dry-run) */}
				<Box sx={{ mt: 2 }}>
					<Button
						variant="outlined"
						size="small"
						onClick={() => handleTestar(false)}
						disabled={testando}
					>
						{testando ? "Testando..." : "Pré-visualizar ação"}
					</Button>
					{acao === "CHAMAR_API" && (
						<Button
							variant="outlined"
							color="warning"
							size="small"
							sx={{ ml: 1 }}
							onClick={() => handleTestar(true)}
							disabled={testando}
						>
							Enviar teste real
						</Button>
					)}
					{resultadoTeste && (
						<Box component="pre" sx={styles.resultadoTeste}>
							{JSON.stringify(resultadoTeste, null, 2)}
						</Box>
					)}
				</Box>
			</Box>

			<Divider />

			<Box sx={styles.rodape}>
				<Button onClick={onClose} disabled={salvando}>
					Cancelar
				</Button>
				<Button variant="contained" onClick={handleSalvar} disabled={salvando}>
					{salvando ? "Salvando..." : "Salvar"}
				</Button>
			</Box>
		</Drawer>
	);
}
